import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useEditContext, useGetRecordRepresentation, useTranslate } from 'react-admin'
import { useFormContext } from 'react-hook-form'

import FailedMembershipActions from './FailedMembershipActions'
import useMemberGroupDetails from './useMemberGroupDetails'

const FailedMembershipAlert = ({
  groupId,
  groupName,
  memberResource,
  removedIds,
  canReadMembers,
  canAssignMembers,
  onRemoved,
  deleting = false,
}) => {
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const { refetch } = useEditContext()
  const { reset } = useFormContext()
  const [assignedIds, setAssignedIds] = useState([])
  const getRepresentation = useGetRecordRepresentation(memberResource)
  const { data: members, isFetching } = useMemberGroupDetails(memberResource, removedIds, {
    enabled: canReadMembers === true,
  })
  const membershipSource = memberResource === 'users' ? 'user_group_ids' : 'vehicle_group_ids'
  const membersById = new Map(members.map((member) => [String(member.id), member]))
  const remainingIds = removedIds.filter((id) => !assignedIds.includes(String(id)))
  const hasCompleteMemberships =
    (!isFetching || assignedIds.length > 0) &&
    removedIds.every((id) => Array.isArray(membersById.get(String(id))?.[membershipSource]))
  const problemIds = remainingIds.filter((id) => {
    const groups = membersById.get(String(id))?.[membershipSource]
    return groups?.length === 1 && String(groups[0]) === String(groupId)
  })
  const identified = hasCompleteMemberships && problemIds.length > 0
  const resolved = assignedIds.length > 0 && hasCompleteMemberships && problemIds.length === 0
  const displayedIds = resolved ? [] : identified ? problemIds : remainingIds
  const messageKey = resolved
    ? deleting
      ? 'retryDeletion'
      : 'retryRemoval'
    : identified
      ? deleting
        ? 'lastMembershipDeletionWarning'
        : 'lastMembershipWarning'
      : deleting
        ? 'lastMembershipDeletionUncertain'
        : 'lastMembershipUncertain'

  return (
    <Alert
      severity={resolved ? 'info' : 'error'}
      sx={{
        mb: 2,
        width: '100%',
        boxSizing: 'border-box',
        flexDirection: { xs: 'column', sm: 'row' },
        '& .MuiAlert-icon': { mb: { xs: 1, sm: 0 }, mr: { xs: 0, sm: 1.5 } },
        '& .MuiAlert-message': { width: '100%', overflow: 'visible', overflowWrap: 'anywhere' },
      }}
    >
      <Typography variant="body2">
        {translate(`mymove.accessGroups.${messageKey}${resolved ? `.${memberResource}` : ''}`, { name: groupName })}
      </Typography>
      {canReadMembers && isFetching && <CircularProgress size={16} color="inherit" sx={{ mt: 1 }} />}
      {displayedIds.length > 0 && (
        <Box component="ul" sx={{ mb: 0, pl: 2 }}>
          {displayedIds.map((id) => (
            <li key={id}>{membersById.has(String(id)) ? getRepresentation(membersById.get(String(id))) : id}</li>
          ))}
        </Box>
      )}
      {identified && (
        <FailedMembershipActions
          currentGroupId={groupId}
          currentGroupName={groupName}
          memberResource={memberResource}
          members={problemIds.map((id) => ({ id: String(id), name: getRepresentation(membersById.get(String(id))) }))}
          canUpdate={canAssignMembers}
          deleting={deleting}
          onRemoved={async () => {
            const { data } = await refetch()
            if (data) reset(data)
            onRemoved()
          }}
          onAssigned={(ids) => {
            setAssignedIds((assigned) => [...new Set([...assigned, ...ids.map(String)])])
            queryClient.invalidateQueries({ queryKey: [memberResource, 'membershipDetails'] })
          }}
        />
      )}
      {assignedIds.length > 0 && !resolved && !deleting && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {translate(`mymove.accessGroups.retryRemoval.${memberResource}`, { name: groupName })}
        </Typography>
      )}
    </Alert>
  )
}

export default FailedMembershipAlert
